import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'components';
import './DatePicker.scss';

/**
 * DatePicker Component
 *
 * A reusable date picker that displays formatted dates (e.g., "30 Apr 2024")
 * and opens the native browser calendar on click.
 *
 * @component
 * @example
 * ```jsx
 * <DatePicker
 *   value="2024-04-30"
 *   onChange={(isoDate) => console.log(isoDate)}
 *   placeholder="Select date"
 * />
 * ```
 *
 * Features:
 * - Displays formatted date (e.g., "30 Apr 2024") inside input-like box
 * - Opens native browser calendar on click
 * - Stores and returns ISO date format (YYYY-MM-DD)
 * - Optional calendar button for explicit picker opening
 * - Fully accessible with ARIA labels
 * - Responsive design
 */
export const DatePicker = ({
  value = '',
  onChange,
  placeholder = 'Select date',
  className = '',
  disabled = false,
  showCalendarButton = true,
  ariaLabel = 'Date picker',
}) => {
  const inputRef = useRef(null);

  const formatISOToDisplay = (isoDate) => {
    if (typeof isoDate !== 'string' || !isoDate) return '';
    const d = new Date(`${isoDate}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d);
  };

  const handleOpenDatePicker = () => {
    if (disabled || !inputRef.current) return;

    if (typeof inputRef.current.showPicker === 'function') {
      try {
        inputRef.current.showPicker();
        return;
      } catch (err) {
        // Ignore - some browsers block showPicker
      }
    }

    inputRef.current.focus();
    try {
      inputRef.current.click();
    } catch (err) {
      // Ignore
    }
  };

  const handleChange = (e) => {
    if (onChange && typeof onChange === 'function') {
      onChange(e.target.value);
    }
  };

  const displayValue = formatISOToDisplay(value) || placeholder;

  return (
    <div className={`date-picker ${className}`}>
      <div
        className={`date-picker-box ${
          disabled ? 'date-picker-box--disabled' : ''
        }`}
        onClick={handleOpenDatePicker}
      >
        <span className='date-picker-value' aria-hidden='true'>
          {displayValue}
        </span>
        <input
          type='date'
          className='date-picker-native-input'
          value={value}
          onChange={handleChange}
          disabled={disabled}
          aria-label={ariaLabel}
          ref={inputRef}
        />
      </div>
      {showCalendarButton && (
        <button
          type='button'
          className='date-picker-button'
          onClick={handleOpenDatePicker}
          disabled={disabled}
          aria-label='Open calendar'
        >
          <Icon name='Calendar' size='sm' />
        </button>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  showCalendarButton: PropTypes.bool,
  ariaLabel: PropTypes.string,
};
